// walletSources.js — client side of the wallet map. The ONE place the app
// asks for another wallet's holdings / co-traders / funding. Everything goes
// through the session-gated Cloudflare function /api/wallet/* which adds the
// data-server secret; the provider (Helius today) is chosen server-side in
// wallet_sources.py, so swapping it never touches the client.

const BASE = '/api/wallet'
const ADDR_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/

export const isWalletAddress = (a) => typeof a === 'string' && ADDR_RE.test(a)

// Small client memo so re-opening the same wallet within a minute is instant
// (the server's shared cache is the real one: 5 min holdings, 24 h funding).
const memo = new Map()
const MEMO_MS = 60_000

async function get(kind, params, { force = false } = {}) {
  const qs = new URLSearchParams(params).toString()
  const key = `${kind}?${qs}`
  const hit = memo.get(key)
  if (!force && hit && Date.now() - hit.at < MEMO_MS) return hit.data
  let res
  try {
    res = await fetch(`${BASE}/${kind}?${qs}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
  } catch {
    throw Object.assign(new Error('network error'), { status: 0 })
  }
  let data = null, text = ''
  try { text = await res.text(); data = JSON.parse(text) } catch {}
  if (!res.ok) {
    // Activity shows the raw status + body so failures can be diagnosed.
    if (kind === 'activity' || kind === 'absorbers') {
      throw Object.assign(new Error(`HTTP ${res.status}: ${(data?.error || text || res.statusText || '').toString().slice(0, 200)}`), { status: res.status })
    }
    const msg = res.status === 401 ? 'sign in to view wallet details'
      : res.status === 429 ? 'busy, try again in a moment'
      : res.status === 503 ? 'wallet data unavailable'
      : (data?.error || `HTTP ${res.status}`)
    throw Object.assign(new Error(msg), { status: res.status })
  }
  memo.set(key, { at: Date.now(), data })
  if (memo.size > 200) memo.delete(memo.keys().next().value)
  return data
}

export const fetchWalletHoldings = (addr, opts) => get('holdings', { addr }, opts)
export const fetchWalletGraph = (addr, hours = 72, opts) => get('graph', { addr, hours: String(hours) }, opts)
export const fetchWalletFunding = (addr, opts) => get('funding', { addr }, opts)
// Coordinated-wallet patterns (0 credits, server cache 10 min).
export const fetchCoinPatterns = (mint, hours = 72, opts) => get('patterns', { mint, hours: String(hours) }, opts)
export const fetchWalletClusters = (hours = 24, addr = '', opts) => get('clusters', addr ? { hours: String(hours), addr } : { hours: String(hours) }, opts)

// Per-wallet trades for the Timeline / Clock views (0 credits, max 60 wallets).
export const fetchWalletActivity = (addrs, hours = 24, mint = '', opts) => {
  const p = { addrs: [...new Set(addrs)].sort().slice(0, 60).join(','), hours: String(hours) }
  if (mint) p.mint = mint
  return get('activity', p, opts)
}

// Who absorbed on one footprint Absorption cell (+ who came back later).
// side: 'bid' (sells absorbed -> buyers) | 'ask' (buys absorbed -> sellers).
// lo/hi are in the chart's price units (swaps.price). 0 credits.
export const fetchAbsorbers = ({ mint, t0, t1, side, lo, hi, after_h = 6 }, opts) => {
  const num = (v) => (Number.isFinite(v) ? String(+v.toPrecision(12)) : '0')
  return get('absorbers', { mint, t0: String(Math.floor(t0)), t1: String(Math.floor(t1)), side,
    lo: num(Math.min(lo, hi)), hi: num(Math.max(lo, hi)), after_h: String(Math.round(after_h)) }, opts)
}

// Open the wallet panel from anywhere (followed list, tape, wallet stats).
export function openWalletMap(address) {
  if (!isWalletAddress(address)) return false
  window.dispatchEvent(new CustomEvent('solcheddar-open-wallet', { detail: { address } }))
  return true
}

// hold-pct-big-20261016: % of a churn/absorption zone's absorbers still in.
// boxes = [{ t0, t1, side: 'b' | 's' }] (max 40). Result keyed "t0:t1:side".
// Always fresh: the server keeps incremental per-box state.
export const fetchHoldPct = ({ mint, boxes }) =>
  get('holdpct', { mint, b: boxes.map(x => `${Math.floor(x.t0)}:${Math.floor(x.t1)}:${x.side}`).join(',') }, { force: true })
